"use client";

import { ProfileStats } from "@/features/gamification";
import { FeedbackShareDrawer } from "@/features/feedback";
import {
  PROFILE_SHAPING_LABELS,
  NeedsAnchorReview,
} from "@/features/profile-shaping";
import { UploadResumeForm } from "@/features/profile";
import { SituationSubmitForm } from "@/features/situations";
import { ThoughtAnchorWorkflow } from "@/features/studio";
import { TargetSubmitForm } from "@/features/targets";
import { ManageVersion } from "@/features/versioning";
import {
  useInterimStateManager,
  useUserChoices,
  useUserProfile,
} from "@/state/globalStore";
import WithDrawerOpen from "@/shared/ui/gestures/WithDrawerOpen";

export default function PrivateProfileDrawers() {
  const { userProfile } = useUserProfile();
  const preferredLanguage =
    userProfile.data.print_settings.print_language ?? "en";
  const { interimState, setInterimState, resetInterimState } =
    useInterimStateManager();
  const { activePrototypeCategory } = useUserChoices();
  const manageVersionMode =
    interimState.manage_version?.value ?? "load_selection";
  const manageVersionTitle =
    manageVersionMode === "save_selection" ||
    manageVersionMode === "extend_selection"
      ? "Save profile selection"
      : "Load profile selection";

  return (
    <>
      <WithDrawerOpen
        title={PROFILE_SHAPING_LABELS.addSituation}
        isOpen={interimState.add_situation?.isOpen ?? false}
        onOpenChangeAction={(isOpen) => {
          setInterimState("add_situation", { isOpen });
        }}
        onCloseAction={() => {
          resetInterimState("add_situation");
        }}
      >
        <SituationSubmitForm />
      </WithDrawerOpen>

      <WithDrawerOpen
        title={PROFILE_SHAPING_LABELS.addFocus}
        isOpen={interimState.add_target?.isOpen ?? false}
        onOpenChangeAction={(isOpen) => {
          setInterimState("add_target", { isOpen });
        }}
        onCloseAction={() => {
          resetInterimState("add_target");
        }}
      >
        <TargetSubmitForm
          onTargetSubmittedSuccess={() => {
            resetInterimState("add_target");
          }}
        />
      </WithDrawerOpen>

      <WithDrawerOpen
        title={PROFILE_SHAPING_LABELS.editFocus}
        isOpen={interimState.edit_target?.isOpen ?? false}
        onOpenChangeAction={(isOpen) => {
          setInterimState("edit_target", {
            isOpen,
            passedObject: interimState.edit_target?.passedObject,
          });
        }}
        onCloseAction={() => {
          resetInterimState("edit_target");
        }}
      >
        <TargetSubmitForm
          targetToEdit={interimState.edit_target?.passedObject}
          onTargetSubmittedSuccess={() => {
            resetInterimState("edit_target");
          }}
        />
      </WithDrawerOpen>

      <WithDrawerOpen
        title={PROFILE_SHAPING_LABELS.reviewNeedsAnchor}
        isOpen={interimState.needs_anchor_review?.isOpen ?? false}
        onOpenChangeAction={(isOpen) => {
          setInterimState("needs_anchor_review", { isOpen });
        }}
        onCloseAction={() => {
          resetInterimState("needs_anchor_review");
        }}
      >
        <NeedsAnchorReview prototype={activePrototypeCategory} />
      </WithDrawerOpen>

      <WithDrawerOpen
        title={PROFILE_SHAPING_LABELS.anchorToSituation}
        isOpen={interimState.reassign_thought?.isOpen ?? false}
        onOpenChangeAction={(isOpen) => {
          setInterimState("reassign_thought", {
            isOpen,
            source: interimState.reassign_thought?.source,
            values: interimState.reassign_thought?.values,
          });
        }}
        onCloseAction={() => {
          resetInterimState("reassign_thought");
        }}
      >
        {interimState.reassign_thought?.values &&
        interimState.reassign_thought.values.length > 0 ? (
          <ThoughtAnchorWorkflow
            items={interimState.reassign_thought.values}
            clearSelectionOnSuccess={
              interimState.reassign_thought.source === "needs_anchor_review"
            }
          />
        ) : null}
      </WithDrawerOpen>

      <WithDrawerOpen
        title="Profile stats"
        isOpen={interimState.profile_stats?.isOpen ?? false}
        onOpenChangeAction={(isOpen) => {
          setInterimState("profile_stats", { isOpen });
        }}
        onCloseAction={() => {
          resetInterimState("profile_stats");
        }}
      >
        <ProfileStats />
      </WithDrawerOpen>

      <WithDrawerOpen
        title="Collect feedback"
        isOpen={interimState.feedback_share?.isOpen ?? false}
        onOpenChangeAction={(isOpen) => {
          setInterimState("feedback_share", { isOpen });
        }}
        onCloseAction={() => {
          resetInterimState("feedback_share");
        }}
      >
        <FeedbackShareDrawer />
      </WithDrawerOpen>

      <WithDrawerOpen
        title="Shape from CV"
        isOpen={interimState.parse_resume?.isOpen ?? false}
        onOpenChangeAction={(isOpen) => {
          setInterimState("parse_resume", { isOpen });
        }}
        onCloseAction={() => {
          resetInterimState("parse_resume");
        }}
      >
        <UploadResumeForm
          preferredLanguage={preferredLanguage}
          onSuccessParsing={() => {
            resetInterimState("parse_resume");
          }}
        />
      </WithDrawerOpen>

      <WithDrawerOpen
        title={manageVersionTitle}
        isOpen={interimState.manage_version?.isOpen ?? false}
        onOpenChangeAction={(isOpen) => {
          setInterimState("manage_version", { isOpen });
        }}
        onCloseAction={() => {
          resetInterimState("manage_version");
        }}
      >
        <ManageVersion
          mode={manageVersionMode}
          onClose={() => {
            resetInterimState("manage_version");
          }}
        />
      </WithDrawerOpen>
    </>
  );
}
